import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { client } from '@Utils'

export const CoursesMenu = ({ coursenum }) => {
  const [title, setTitle] = useState('')
  const [books, setBooks] = useState([])
  const [hasArchivedCourses, setArchivedCoursesFlag] = useState(false)
  const [open, setOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  const menuRef = useRef(null)
  const buttonRef = useRef(null)

  // Fetch course + books data
  useEffect(() => {
    let mounted = true
    async function fetchData() {
      try {
        const resp = await client.get(
          '/reader/web.get_books/' + coursenum + '/'
        )
        if (!mounted) return
        setTitle(resp.data.course_title)
        setBooks(resp.data.books || [])
        setArchivedCoursesFlag(resp.data.has_archived_courses)
      } catch (err) {
        console.warn(err)
      }
    }
    fetchData()
    return () => {
      mounted = false
    }
  }, [coursenum])

  // Update menu position when opening or scrolling
  useEffect(() => {
    if (open && buttonRef.current) {
      const updatePosition = () => {
        const rect = buttonRef.current.getBoundingClientRect()
        setMenuPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        })
      }

      updatePosition()

      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [open])

  // Close menu when clicking outside
  useEffect(() => {
    function onClickOutside(e) {
      if (
        open &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    // Use mousedown with slight delay to allow navigation to complete
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  // Close menu when drawer closes
  useEffect(() => {
    function handleDrawerClose() {
      setOpen(false)
    }
    window.addEventListener('close-left-drawer', handleDrawerClose)
    return () =>
      window.removeEventListener('close-left-drawer', handleDrawerClose)
  }, [])

  // Keyboard navigation
  const handleKeyDown = useCallback(function (e) {
    const menu = menuRef.current
    if (!menu) return
    const items = Array.from(menu.querySelectorAll('[role="menuitem"]'))
    if (!items.length) return

    const currentIndex = items.indexOf(document.activeElement)
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[(currentIndex + 1) % items.length].focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[(currentIndex - 1 + items.length) % items.length].focus()
        break
      case 'Home':
        e.preventDefault()
        items[0].focus()
        break
      case 'End':
        e.preventDefault()
        items[items.length - 1].focus()
        break
      case 'Escape':
        setOpen(false)
        if (buttonRef.current) buttonRef.current.focus()
        break
      case 'Tab':
        // Close the menu at the boundaries instead of wrapping focus back
        // inside it. Both directions explicitly refocus the trigger button
        // rather than letting the browser's native Tab handling run:
        // closing the menu unmounts the focused item, and if that node is
        // detached from the DOM before the browser's default Tab action
        // executes, focus resets to <body> first and the browser then
        // jumps to the first focusable element on the whole page instead
        // of continuing naturally from the menu.
        if (e.shiftKey && currentIndex === 0) {
          e.preventDefault()
          setOpen(false)
          buttonRef.current && buttonRef.current.focus()
        } else if (!e.shiftKey && currentIndex === items.length - 1) {
          e.preventDefault()
          setOpen(false)
          buttonRef.current && buttonRef.current.focus()
        }
        break
      default:
        break
    }
  }, [])

  function toggleMenu() {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }

    setOpen(!open)

    if (!open) {
      setTimeout(function () {
        const first =
          menuRef.current && menuRef.current.querySelector('[role="menuitem"]')
        if (first) first.focus()
      }, 0)
    }
  }

  // Prevent menu interactions from closing the drawer
  const handleMenuMouseDown = useCallback(e => {
    // Stop propagation to prevent drawer's click-outside handler
    e.stopPropagation()
  }, [])

  const handleMenuContextMenu = useCallback(e => {
    // Stop propagation on right-click
    e.stopPropagation()
  }, [])

  const handleMenuClick = useCallback(e => {
    // Stop propagation when clicking inside menu
    e.stopPropagation()
  }, [])

  const wrapperStyle = {
    position: 'relative',
    display: 'flex',
    width: '100%',
    padding: '0px 12px',
  }

  const buttonStyle = {
    padding: '8px 12px',
    marginTop: '12px',
    width: '100%',
    borderRadius: 6,
    border: '1px solid #ccc',
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  }

  const itemStyle = {
    display: 'block',
    fontFamily: 'var(--font-ui)',
    width: '100%',
    padding: '8px 12px',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#111827',
    textDecoration: 'none',
    outline: 'none',
  }

  const btnText = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: '1 1 0',
    minWidth: '0',
    textAlign: 'left',
  }

  return (
    <React.Fragment>
      <div
        className="CourseNavMenuWrapper justify-content-center"
        style={wrapperStyle}
      >
        <button
          ref={buttonRef}
          className="CourseNavMenuButton"
          aria-haspopup="true"
          aria-label="Course navigation"
          aria-expanded={open ? 'true' : 'false'}
          onClick={toggleMenu}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              toggleMenu()
            }
          }}
          style={buttonStyle}
        >
          <span style={btnText}>{title}</span>
          <i
            className="fa-light fa-chevron-down"
            style={{ flexShrink: 0 }}
            aria-hidden="true"
          ></i>
        </button>
      </div>

      {open &&
        createPortal(
          <ul
            className="dropdown-menu show"
            role="menu"
            aria-label="Courses navigation"
            ref={menuRef}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMenuMouseDown}
            onContextMenu={handleMenuContextMenu}
            onClick={handleMenuClick}
            style={{
              display: 'block',
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              zIndex: 1000010,
              background: '#fff',
              border: '1px solid #ddd',
              boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
              borderRadius: 6,
              padding: '6px 0',
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {books.map(book => (
              <li key={book.coursenum} className="me-list-item" role="none">
                <a
                  className="menulink"
                  href={`/reader/web/${book.coursenum}/`}
                  role="menuitem"
                  style={itemStyle}
                  onMouseEnter={function (e) {
                    e.currentTarget.style.background = '#F3F3F3'
                  }}
                  onMouseLeave={function (e) {
                    e.currentTarget.style.background = 'transparent'
                  }}
                  onFocus={function (e) {
                    e.currentTarget.style.background = '#F3F3F3'
                  }}
                  onBlur={function (e) {
                    e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {book.title}
                </a>
              </li>
            ))}
            {hasArchivedCourses && (
              <React.Fragment>
                <li className="divider"></li>
                <li className="me-list-item" role="none">
                  <a
                    className="menulink"
                    href="/account/index/#archived_header"
                    role="menuitem"
                    style={itemStyle}
                    onMouseEnter={function (e) {
                      e.currentTarget.style.background = '#F3F3F3'
                    }}
                    onMouseLeave={function (e) {
                      e.currentTarget.style.background = 'transparent'
                    }}
                    onFocus={function (e) {
                      e.currentTarget.style.background = '#F3F3F3'
                    }}
                    onBlur={function (e) {
                      e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    View archived courses{' '}
                    <span className="fa-light fa-long-arrow-right ml-2"></span>
                  </a>
                </li>
              </React.Fragment>
            )}
          </ul>,
          document.getElementById('courses-menu-root') || document.body
        )}
    </React.Fragment>
  )
}
